// The shared deposit-account contract. Admin uses this same module; the server
// validates its own copy of the contract before any automated processing.
const BANK_NAMES = {
  scb: 'ธนาคารไทยพาณิชย์', kbank: 'ธนาคารกสิกรไทย', bankokbank: 'ธนาคารกรุงเทพ',
  ktb: 'ธนาคารกรุงไทย', krungsri: 'ธนาคารกรุงศรีอยุธยา', ttb: 'ธนาคารทหารไทยธนชาต',
  gsb: 'ธนาคารออมสิน', baac: 'ธนาคาร ธ.ก.ส.', cimb: 'ธนาคารซีไอเอ็มบี', uob: 'ธนาคารยูโอบี',
  kiatnakin: 'ธนาคารเกียรตินาคิน', lh: 'ธนาคารแลนด์ แอนด์ เฮ้าส์', clicx: 'ธนาคารคลิกซ์ (CLICX)'
}
const CHANNEL_NAMES = { SMS: 'SMS', SCAN_SLIP: 'Scan Slip', LINE: 'LINE' }
const digits = value => String(value || '').replace(/\D/g, '')
const normalizeBankCode = value => {
  const code = String(value || '').toLowerCase()
  return ({ bbl: 'bankokbank', bangkokbank: 'bankokbank', bay: 'krungsri' })[code] || code
}
const allowedChannels = bank => bank === 'scb' ? ['SMS', 'SCAN_SLIP'] : bank === 'gsb' ? ['LINE', 'SCAN_SLIP'] : ['SCAN_SLIP']
const isUnified = deposit => deposit.accountsVersion === 2
const normalizeAccount = (raw, id, bankCode) => {
  const code = normalizeBankCode(bankCode || raw.bankCode)
  const number = digits(raw.accountNo || raw.accountno)
  const autoChannel = raw.autoChannel || (Array.isArray(raw.channels) ? raw.channels[0] : '') || allowedChannels(code)[0]
  return {
    accountId: raw.accountId || raw.id || id || `custom-${code}-${number}`,
    bankCode: code, accountNo: number, accountno: number,
    accountName: raw.accountName || `${raw.firstname || ''} ${raw.lastname || ''}`.trim(),
    label: raw.label || raw.nickname || BANK_NAMES[code] || code,
    autoChannel, channels: [autoChannel], active: raw.active !== false,
    showOnWeb: raw.showOnWeb !== false,
    schedule: { mode: (raw.schedule || {}).mode || 'always', days: Object.values((raw.schedule || {}).days || ((raw.schedule || {}).mode === 'scheduled' ? [] : [0, 1, 2, 3, 4, 5, 6])), windows: Object.values((raw.schedule || {}).windows || {}) }
  }
}
const allAccounts = (deposit = {}) => {
  if (isUnified(deposit)) return Object.values(deposit.accounts || {}).filter(Boolean).map(a => normalizeAccount(a))
  const custom = Object.values(deposit.customBanks || {}).filter(Boolean).map(a => normalizeAccount({ ...a, autoChannel: 'SCAN_SLIP' }))
  const legacy = Object.keys(deposit).filter(key => /^(scb|gsb)\d*$/.test(key)).filter(key => digits((deposit[key] || {}).accountno || (deposit[key] || {}).accountNo)).map(key => {
    const bank = key.startsWith('gsb') ? 'gsb' : 'scb', raw = deposit[key]
    const show = typeof raw.show_frontend === 'boolean' ? raw.show_frontend : (deposit[bank] || {}).show_frontend !== false
    return normalizeAccount({ ...raw, showOnWeb: show, autoChannel: bank === 'gsb' ? 'LINE' : 'SMS', label: key.toUpperCase() }, `legacy-${key}`, bank)
  })
  const identities = new Set(legacy.map(a => `${a.bankCode}:${a.accountNo}`))
  return [...legacy, ...custom.filter(a => !identities.has(`${a.bankCode}:${a.accountNo}`))]
}
const minutes = value => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5))
const validTime = value => typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
const scheduleValid = schedule => !!schedule && ['always', 'scheduled'].includes(schedule.mode) &&
  (schedule.mode === 'always' || (Array.isArray(schedule.days) && schedule.days.length > 0 && schedule.days.every(d => Number.isInteger(d) && d >= 0 && d <= 6) &&
    Array.isArray(schedule.windows) && schedule.windows.length > 0 && schedule.windows.every(w => validTime(w.start) && validTime(w.end) && w.start !== w.end)))
const withinSchedule = (account, now = Date.now()) => {
  const schedule = account.schedule
  if (!scheduleValid(schedule)) return false
  if (schedule.mode === 'always') return true
  const thai = new Date(Number(now) + 7 * 3600000), day = thai.getUTCDay(), time = thai.getUTCHours() * 60 + thai.getUTCMinutes()
  return schedule.windows.some(w => {
    const start = minutes(w.start), end = minutes(w.end)
    return start < end ? schedule.days.includes(day) && time >= start && time < end :
      (schedule.days.includes(day) && time >= start) || (schedule.days.includes((day + 6) % 7) && time < end)
  })
}
const visibleAccounts = (deposit, now = Date.now()) => allAccounts(deposit).filter(a => a.active && a.showOnWeb && a.accountNo &&
  BANK_NAMES[a.bankCode] && allowedChannels(a.bankCode).includes(a.autoChannel) && withinSchedule(a, now))
const validationError = accounts => {
  const ids = new Set(), numbers = new Set()
  for (const a of accounts) {
    const key = `${a.bankCode}:${digits(a.accountNo || a.accountno)}`
    if (!a.accountId || !/^[A-Za-z0-9_-]+$/.test(a.accountId) || ids.has(a.accountId)) return 'รหัสบัญชีซ้ำหรือไม่ถูกต้อง'
    if (!BANK_NAMES[a.bankCode] || !String(a.accountName || '').trim() || digits(a.accountNo || a.accountno).length < 6) return 'กรอกธนาคาร ชื่อเจ้าของบัญชี และเลขบัญชีให้ครบ'
    if (numbers.has(key)) return 'ธนาคารและเลขบัญชีซ้ำกัน'
    if (!allowedChannels(a.bankCode).includes(a.autoChannel)) return 'ช่องทาง Auto ไม่รองรับธนาคารนี้'
    if (!scheduleValid(a.schedule)) return 'กรอกวันและช่วงเวลาให้ครบ เวลาเริ่มและหยุดต้องต่างกัน'
    ids.add(a.accountId); numbers.add(key)
  }
  return ''
}
const accountSnapshot = account => ({ accountId: account.accountId, bankCode: account.bankCode, accountNo: digits(account.accountNo || account.accountno),
  accountName: account.accountName, label: account.label || '', autoChannel: account.autoChannel })
const receivingMetadata = (account, channel = 'MANUAL') => ({ mybank: account.bankCode, bankdeposit_accountno: digits(account.accountNo || account.accountno),
  depositAccountId: account.accountId, depositAccountSnapshot: accountSnapshot(account), depositSourceChannel: channel })
const receiverBank = row => (row.depositAccountSnapshot || {}).bankCode || row.mybank || ''
const receiverNumber = row => (row.depositAccountSnapshot || {}).accountNo || row.bankdeposit_accountno || ''
const receiverLabel = row => {
  const a = row.depositAccountSnapshot || {}
  return [a.label || a.accountName, receiverNumber(row)].filter(Boolean).join(' · ') || 'ไม่ระบุ (รายการเดิม)'
}
export { BANK_NAMES, CHANNEL_NAMES, digits, normalizeBankCode, allowedChannels, isUnified, normalizeAccount, allAccounts,
  scheduleValid, withinSchedule, visibleAccounts, validationError, accountSnapshot, receivingMetadata, receiverBank, receiverNumber, receiverLabel }
